import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, User, ArrowLeft, Loader2 } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#EAF1E8] flex items-center justify-center px-6 py-12"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#4A6B53] hover:text-[#2A4D35] mb-10"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="bg-[#FDFDF9] border border-[#183321]/10 rounded-sm p-8 md:p-10">
          <div className="flex items-baseline gap-1 mb-2">
            <span className="font-serif text-xl font-semibold tracking-wide text-[#183321] uppercase">
              Josengineer
            </span>
            <span className="font-serif text-xl italic text-[#2A4D35] uppercase">
              Corp
            </span>
            <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#C26D5C]" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#183321] tracking-tight leading-tight">
            Admin sign in
          </h1>
          <p className="text-sm text-[#4A6B53] mt-2">
            Acceso interno · upload project photos.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                Username
              </label>
              <div className="relative">
                <User
                  size={16}
                  className="absolute left-0 top-3.5 text-[#4A6B53]"
                />
                <input
                  data-testid="login-input-username"
                  type="text"
                  required
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-transparent border-b border-[#183321]/30 pl-7 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-0 top-3.5 text-[#4A6B53]"
                />
                <input
                  data-testid="login-input-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-b border-[#183321]/30 pl-7 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35] transition-colors"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-[#C26D5C] bg-[#C26D5C]/10 border border-[#C26D5C]/30 rounded-sm px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#2A4D35] text-[#FDFDF9] text-sm uppercase tracking-widest font-semibold rounded-sm hover:bg-[#3C664A] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
